import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import Post from './Post';
import FollowButton from './FollowButton';
import ProfilePicture from './ProfilePicture';
import { 
  FaMapMarkerAlt, 
  FaEnvelope, 
  FaCalendarAlt, 
  FaUserFriends, 
  FaUserEdit, 
  FaHeart, 
  FaRegHeart, 
  FaCamera,
  FaCrown,
  FaPaypal,
  FaUserTag,
  FaEdit,
  FaCreditCard
} from 'react-icons/fa';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove,
  orderBy,
  limit,
  addDoc,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { 
  checkUserRole, 
  canEditProfile, 
  isUserProfileOwner, 
  canViewProfileCredits 
} from '../utils/userManagement';
import { FEATURE_PRICING } from '../utils/stripe';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNotifications } from '../contexts/NotificationContext'; // Import the useNotifications hook
import { loadScript } from "@paypal/paypal-js"; // Import PayPal script loading function
import { createNotification } from '../contexts/NotificationContext'; // Adjust the import path as needed

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10">
    <div className="absolute top-0 left-0 w-full h-full">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className={`
            absolute rounded-full mix-blend-screen filter blur-xl opacity-30 animate-pulse
            ${i % 2 === 0 ? 'bg-purple-500' : 'bg-pink-500'}
          `}
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            width: `${Math.random() * 400 + 200}px`,
            height: `${Math.random() * 400 + 200}px`,
            animationDelay: `${i * 0.5}s`,
            animationDuration: `${Math.random() * 10 + 10}s`
          }}
        />
      ))}
    </div>
  </div>
);

const ProUpgradeCTA = ({ user, profile, isOwnProfile }) => {
  const { createNotification } = useNotifications();
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [showPayPalConfirmModal, setShowPayPalConfirmModal] = useState(false);
  const [payPalTransactionId, setPayPalTransactionId] = useState('');
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  // Function to hide the banner
  const hideBanner = () => {
    setIsBannerVisible(false);
  };

  if (!isBannerVisible) return null;

  const handleStripePayment = async () => {
    if (!user) {
      alert('Please log in to upgrade to Pro');
      return;
    }

    try {
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        lineItems: [{
          price: 'price_1QucBUFoEJrwHsIZYwq5NsmV',
          quantity: 1,
        }],
        mode: 'subscription',
        successUrl: `${window.location.origin}/profile?payment=success`,
        cancelUrl: `${window.location.origin}/profile?payment=cancelled`,
        clientReferenceId: user.uid,
        customerEmail: user.email
      });

      if (error) {
        console.error('Stripe Checkout error:', error);
        alert('Payment processing failed. Please try again.');
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      alert('Failed to process payment. Please try again.');
    }
  };

  const handlePayPalPayment = () => {
    if (!user) {
      alert('Please log in to upgrade to Pro');
      return;
    }

    // Open PayPal payment in a new window
    const paypalPaymentUrl = `https://www.paypal.com/paypalme/jesterland/8`;
    window.open(paypalPaymentUrl, '_blank');

    // Prompt for transaction ID
    const payPalTransactionId = prompt(
      'Please complete your payment of €8 for Pro subscription.\n\nAfter payment, enter the PayPal Transaction ID:'
    );

    if (payPalTransactionId) {
      // Create a pending upgrade request in Firestore
      addDoc(collection(db, 'upgradeRequests'), {
        userId: user.uid,
        email: user.email,
        requestedRole: 'pro',
        currentRole: profile?.role,
        paymentMethod: 'paypal',
        amount: 8,
        paypalTransactionId: payPalTransactionId,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      // Notify user
      createNotification(
        user.uid,
        'pro_upgrade_pending',
        'Your Pro upgrade request is pending verification',
        null
      );

      alert('Your Pro upgrade request is pending admin verification. You will be notified once approved.');
    }
  };

  return (
    <div className={`${
      isOwnProfile ? 'opacity-50 cursor-not-allowed' : ''
    }`}>
      <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl shadow-2xl p-6 mb-6 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-3xl">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-blue-700 opacity-10"></div>
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0 md:space-x-6">
          <div className="flex-grow text-center md:text-left">
            <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">
              Unlock Dreamarium Pro
            </h2>
            <p className="text-white/80 mb-4 max-w-xl">
              Elevate your dream creation experience with unlimited credits, priority features, and exclusive access.
            </p>
            <ul className="text-white/90 space-y-2 mb-4 inline-block text-left">
              <li className="flex items-center">
                <span className="mr-2">✨</span> Unlimited Monthly Credits
              </li>
              <li className="flex items-center">
                <span className="mr-2">🚀</span> Priority Feature Access
              </li>
              <li className="flex items-center">
                <span className="mr-2">🎨</span> Exclusive Dream Generation Styles
              </li>
            </ul>
          </div>
          <div className="flex flex-col space-y-3 w-full md:w-auto">
            <button
              onClick={handleStripePayment}
              className="w-full md:w-auto px-6 py-3 bg-white text-purple-600 font-bold rounded-full hover:bg-purple-100 transition flex items-center justify-center space-x-2"
            >
              <FaCreditCard className="mr-2" /> 
              Upgrade with Stripe - €8/month
            </button>
            <button
              onClick={handlePayPalPayment}
              className="w-full md:w-auto px-6 py-3 bg-white text-blue-600 font-bold rounded-full hover:bg-blue-100 transition flex items-center justify-center space-x-2"
            >
              <FaPaypal className="mr-2" /> 
              Upgrade with PayPal - €8/month
            </button>
          </div>
        </div>
        <button 
          onClick={hideBanner}
          className="absolute top-4 right-4 text-white text-2xl hover:text-gray-200 transition-colors"
        >
          X
        </button>
      </div>
    </div>
  );
};  

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  selectedPackage, 
  onPaymentSuccess 
}) => {
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const StripePaymentForm = () => {
    const stripe = useStripe();
    const elements = useElements();

    const handleStripeSubmit = async (event) => {
      event.preventDefault();
      
      if (!stripe || !elements) {
        return;
      }

      setIsProcessing(true);
      setPaymentError(null);

      try {
        // Create payment intent on your backend
        const response = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: selectedPackage.price * 100, // Convert to cents
            currency: 'usd',
            credits: selectedPackage.credits
          })
        });

        const { clientSecret } = await response.json();

        // Confirm card payment
        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: auth.currentUser.displayName
            }
          }
        });

        if (result.error) {
          setPaymentError(result.error.message);
          setIsProcessing(false);
        } else {
          // Payment successful
          await updateUserCredits(selectedPackage.credits);
          onPaymentSuccess();
          onClose();
        }
      } catch (error) {
        console.error('Payment error:', error);
        setPaymentError('Payment failed. Please try again.');
        setIsProcessing(false);
      }
    };

    return (
      <form onSubmit={handleStripeSubmit} className="space-y-4">
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
        {paymentError && (
          <div className="text-red-500 text-sm">{paymentError}</div>
        )}
        <button 
          type="submit" 
          disabled={isProcessing || !stripe}
          className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition"
        >
          {isProcessing ? 'Processing...' : `Pay $${selectedPackage.price}`}
        </button>
      </form>
    );
  };

  const updateUserCredits = async (creditsToAdd) => {
    const userDocRef = doc(db, 'profiles', auth.currentUser.uid);
    await updateDoc(userDocRef, {
      credits: increment(creditsToAdd)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            Buy {selectedPackage.credits} Credits
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="mb-4">
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => setPaymentMethod('stripe')}
              className={`py-2 px-4 rounded-lg ${
                paymentMethod === 'stripe' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Credit Card
            </button>
          </div>

          <Elements stripe={stripePromise}>
            <StripePaymentForm />
          </Elements>
        </div>
      </div>
    </div>
  );
};

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [isProfileOwner, setIsProfileOwner] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('public');
  const [isFollowing, setIsFollowing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState('simple');
  const [isEditing, setIsEditing] = useState(false);
  const { createNotification } = useNotifications();
  const [editForm, setEditForm] = useState({
    name: '',
    location: '',
    email: ''
  });
  const [stats, setStats] = useState({
    posts: 0,
    likes: 0,
    followers: 0,
    following: 0
  });
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [isUploadingProfilePic, setIsUploadingProfilePic] = useState(false);
  const [profilePictureError, setProfilePictureError] = useState(null);
  const [canViewCredits, setCanViewCredits] = useState(false);
  const [isCreditsExpanded, setIsCreditsExpanded] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [lastCreditResetDate, setLastCreditResetDate] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [isUploadingCoverImage, setIsUploadingCoverImage] = useState(false);
  const [coverImageError, setCoverImageError] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const [editedAbout, setEditedAbout] = useState(profile?.about || '');
  const [paypalScriptLoaded, setPaypalScriptLoaded] = useState(false); // Add state for PayPal script

  const CREDIT_PACKAGES = [
    { 
      id: 1,
      credits: 50,
      price: 5,
      name: '50 Credits',
      description: 'Starter Pack',
      stripePrice: 'price_1QucBUFoEJrwHsIZYwq5NsmV'
    },
    { 
      id: 2,
      credits: 120,
      price: 10,
      name: '120 Credits',
      description: 'Dreamer Pack',
      stripePrice: 'price_1QucAJFoEJrwHsIZfEhnllSp'
    },
    { 
      id: 3,
      credits: 270,
      price: 20,
      name: '270 Credits',
      description: 'Visionary Pack',
      stripePrice: 'price_1QucAHFoEJrwHsIZgIHQU4Go'
    }
  ];

  const MONTHLY_FREE_CREDITS = 5;

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const userRole = await checkUserRole(user.uid);
          setCurrentUserRole(userRole);
        } catch (error) {
          console.error('Error checking user role:', error);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const checkUserRoleStatus = async () => {
      if (currentUser) {
        try {
          const userRole = await checkUserRole(currentUser.uid);
          setCurrentUserRole(userRole);
        } catch (error) {
          console.error('Error checking user role:', error);
        }
      }
    };

    checkUserRoleStatus();
  }, [currentUser]);

  useEffect(() => {
    const checkProfilePermissions = async () => {
      if (currentUser && profile) {
        const ownerStatus = await isUserProfileOwner(currentUser.uid, username);
        const editPermission = await canEditProfile(currentUser.uid, username);
        const creditsVisibility = await canViewProfileCredits(currentUser.uid, username);
        
        setIsProfileOwner(ownerStatus);
        setCanEdit(editPermission);
        setCanViewCredits(creditsVisibility);
      }
    };

    if (currentUser && profile) {
      checkProfilePermissions();
    }
  }, [currentUser, profile, username]);

  useEffect(() => {
    if (username) {
      fetchProfileData();
    }
  }, [username]);

  useEffect(() => {
    const checkAndResetMonthlyCredits = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const userDocRef = doc(db, 'profiles', user.uid);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.data();

      const now = new Date();
      const lastReset = userData.lastCreditResetDate?.toDate() || new Date(0);
      
      // Check if it's a new month
      if (
        now.getFullYear() !== lastReset.getFullYear() || 
        now.getMonth() !== lastReset.getMonth()
      ) {
        await updateDoc(userDocRef, {
          credits: (userData.credits || 0) + MONTHLY_FREE_CREDITS,
          lastCreditResetDate: serverTimestamp()
        });

        setUserCredits((userData.credits || 0) + MONTHLY_FREE_CREDITS);
        setLastCreditResetDate(now);
      } else {
        setUserCredits(userData.credits || 0);
        setLastCreditResetDate(lastReset);
      }
    };

    checkAndResetMonthlyCredits();
  }, []);

  useEffect(() => {
    const handlePaymentResult = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      const creditsEarned = urlParams.get('credits');

      if (paymentStatus === 'success' && creditsEarned) {
        try {
          // Update user credits in Firestore
          const userDocRef = doc(db, 'profiles', auth.currentUser.uid);
          await updateDoc(userDocRef, {
            credits: increment(parseInt(creditsEarned, 10))
          });

          // Optional: Show success message
          alert(`Successfully purchased ${creditsEarned} credits!`);

          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error updating credits:', error);
        }
      } else if (paymentStatus === 'cancelled') {
        alert('Payment was cancelled.');
      }
    };

    handlePaymentResult();
  }, []);

  useEffect(() => {
    const loadPayPalScriptAsync = async () => {
      try {
        const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
        
        if (!paypalClientId) {
          console.error('PayPal Client ID is not set');
          return;
        }

        await loadScript({ 
          clientId: paypalClientId,
          currency: 'EUR',
          intent: 'capture'
        });
        setPaypalScriptLoaded(true);
      } catch (error) {
        console.error('Failed to load PayPal script', error);
        // alert('Failed to load PayPal payment option. Please try again later.');
      }
    };

    loadPayPalScriptAsync();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      
      const usersRef = collection(db, 'profiles');
      const q = query(usersRef, where('username', '==', username));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const profileDoc = querySnapshot.docs[0];
        const profileData = { id: profileDoc.id, ...profileDoc.data() };
        setProfile(profileData);

        setStats(prevStats => ({
          ...prevStats,
          credits: profileData.credits || 0
        }));

        if (currentUser) {
          const currentUserDoc = await getDoc(doc(db, 'profiles', currentUser.uid));
          if (currentUserDoc.exists()) {
            const following = currentUserDoc.data().following || [];
            setIsFollowing(following.includes(profileDoc.id));
          }
        }

        const postsRef = collection(db, 'posts');
        const q = query(postsRef, where('userId', '==', profileDoc.id), orderBy('createdAt', 'desc'));
        const postsSnap = await getDocs(q);
        const postsData = postsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPosts(postsData);

        const totalLikes = postsData.reduce((sum, post) => sum + (post.likes?.length || 0), 0);
        
        setStats({
          posts: postsData.length,
          likes: totalLikes,
          followers: profileData.followers?.length || 0,
          following: profileData.following?.length || 0
        });
      } else {
        setError('Profile not found');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser || currentUser.uid === profile.uid) {
      return;
    }

    try {
      const currentUserRef = doc(db, 'profiles', currentUser.uid);
      const targetUserRef = doc(db, 'profiles', profile.uid);

      if (isFollowing) {
        await updateDoc(currentUserRef, {
          following: arrayRemove(profile.uid)
        });
        await updateDoc(targetUserRef, {
          followers: arrayRemove(currentUser.uid)
        });
        setIsFollowing(false);
        setStats(prev => ({ ...prev, followers: Math.max(0, prev.followers - 1) }));
      } else {
        await updateDoc(currentUserRef, {
          following: arrayUnion(profile.uid)
        });
        await updateDoc(targetUserRef, {
          followers: arrayUnion(currentUser.uid)
        });
        setIsFollowing(true);
        setStats(prev => ({ ...prev, followers: prev.followers + 1 }));
      }
    } catch (error) {
      console.error('Error updating follow status:', error);
    }
  };

  const handleProfilePictureUpload = async (event) => {
    if (!currentUser || !isProfileOwner) {
      alert('You can only upload a profile picture to your own profile.');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      setProfilePictureError('Invalid file type. Please upload a JPEG, PNG, or GIF.');
      return;
    }

    if (file.size > maxSize) {
      setProfilePictureError('File is too large. Maximum size is 5MB.');
      return;
    }

    try {
      setIsUploadingProfilePic(true);
      setProfilePictureError(null);
      
      const compressedFile = await compressImage(file);
      const filename = `${currentUser.uid}_${Date.now()}_${compressedFile.name}`;
      
      const storage = getStorage();
      const storageRef = ref(storage, `profile_pictures/${currentUser.uid}/${filename}`);
      
      const metadata = {
        contentType: compressedFile.type,
        customMetadata: {
          'uploadedBy': currentUser.uid
        }
      };

      const snapshot = await uploadBytes(storageRef, compressedFile, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      const userDocRef = doc(db, 'profiles', currentUser.uid);
      await updateDoc(userDocRef, {
        profilePic: downloadURL,
        photoURL: downloadURL,
        updatedAt: serverTimestamp()
      });
      
      setProfile(prev => ({
        ...prev,
        profilePic: downloadURL,
        photoURL: downloadURL
      }));
      
      setProfilePictureError(null);
    } catch (error) {
      console.error('Profile picture upload error:', error);
      setProfilePictureError('Failed to upload profile picture. Please try again.');
    } finally {
      setIsUploadingProfilePic(false);
    }
  };

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    };
    
    try {
      return await imageCompression(file, options);
    } catch (error) {
      console.error('Image compression error:', error);
      return file; 
    }
  };

  const handleEdit = async () => {
    if (!currentUser || !isProfileOwner) {
      alert('You can only edit your own profile.');
      return;
    }

    if (isEditing) {
      try {
        await updateDoc(doc(db, 'profiles', profile.uid), {
          name: editForm.name,
          location: editForm.location,
          updatedAt: serverTimestamp()
        });
        setProfile(prev => ({ 
          ...prev, 
          name: editForm.name,
          location: editForm.location 
        }));
        setIsEditing(false);
      } catch (error) {
        console.error('Error updating profile:', error);
        alert('Failed to update profile. Please try again.');
      }
    } else {
      setIsEditing(true);
      setEditForm({
        name: profile?.name || '',
        location: profile?.location || '',
        username: profile?.username || '',
        email: profile?.email || ''
      });
    }
  };

  const handleCreditPurchase = async (packageDetails) => {
    if (!auth.currentUser) {
      alert('Please log in to purchase credits');
      return;
    }

    try {
      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        lineItems: [{
          price: packageDetails.stripePrice, // Stripe Price ID
          quantity: 1,
        }],
        mode: 'payment',
        successUrl: `${window.location.origin}/profile?payment=success&credits=${packageDetails.credits}`,
        cancelUrl: `${window.location.origin}/profile?payment=cancelled`,
        clientReferenceId: auth.currentUser.uid, // Pass user ID
        customerEmail: auth.currentUser.email // Pre-fill email
      });

      if (error) {
        console.error('Stripe Checkout error:', error);
        alert('Payment processing failed. Please try again.');
      }
    } catch (error) {
      console.error('Credit purchase error:', error);
      alert('Failed to process credit purchase. Please try again.');
    }
  };

  const handlePayPalPayment = async (selectedPackage) => {
    if (!currentUser) {
      alert('Please log in to purchase credits');
      return;
    }

    try {
      // Open PayPal payment in a new window
      const paypalPaymentUrl = `https://www.paypal.com/paypalme/jesterland/${selectedPackage.price}`;
      window.open(paypalPaymentUrl, '_blank');

      // Prompt for transaction ID
      const payPalTransactionId = prompt(
        `Please complete your payment of €${selectedPackage.price} for ${selectedPackage.credits} Credits.\n\nAfter payment, enter the PayPal Transaction ID:`
      );

      if (payPalTransactionId) {
        // Create a pending credit request in Firestore
        await addDoc(collection(db, 'pending_credit_requests'), {
          userId: currentUser.uid,
          email: currentUser.email,
          credits: selectedPackage.credits,
          amount: selectedPackage.price,
          paypalTransactionId: payPalTransactionId,
          status: 'pending',
          createdAt: serverTimestamp()
        });

        // Notify user
        await createNotification(
          currentUser.uid,
          'credit_purchase_pending',
          `Credit purchase of ${selectedPackage.credits} credits is pending verification`,
          null
        );

        alert('Your credit purchase is pending admin verification. You will be notified once approved.');
      }
    } catch (error) {
      console.error('PayPal payment error:', error);
      alert('Failed to process payment. Please try again.');
    }
  };

  const handleUpgradeClick = () => {
    setShowPaymentOptions(true);
  };

  const isOwnProfile = currentUser && profile && currentUser.uid === profile.uid;

  const renderUpgradeModal = () => {
    return (
      <ProUpgradeCTA 
        user={currentUser} 
        profile={profile} 
        isOwnProfile={true} 
      />
    );
  };

  const handleCoverImageUpload = async (e) => {
    if (!currentUser || !isProfileOwner) {
      alert('You can only upload a cover image to your own profile.');
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      setCoverImageError('Invalid file type. Please upload a JPEG, PNG, or GIF.');
      return;
    }

    if (file.size > maxSize) {
      setCoverImageError('File is too large. Maximum size is 5MB.');
      return;
    }

    try {
      setIsUploadingCoverImage(true);
      setCoverImageError(null);

      const compressedFile = await compressImage(file);
      const filename = `cover_${currentUser.uid}_${Date.now()}_${compressedFile.name}`;
      
      const storage = getStorage();
      const storageRef = ref(storage, `cover_images/${currentUser.uid}/${filename}`);
      
      const metadata = {
        contentType: compressedFile.type,
        customMetadata: {
          'uploadedBy': currentUser.uid
        }
      };

      const snapshot = await uploadBytes(storageRef, compressedFile, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const userDocRef = doc(db, 'profiles', currentUser.uid);
      await updateDoc(userDocRef, {
        coverImageUrl: downloadURL,
        updatedAt: serverTimestamp()
      });

      setProfile(prev => ({
        ...prev,
        coverImageUrl: downloadURL
      }));

      setCoverImageError(null);
    } catch (error) {
      console.error('Cover image upload error:', error);
      setCoverImageError('Failed to upload cover image. Please try again.');
    } finally {
      setIsUploadingCoverImage(false);
    }
  };

  const handleProSubscriptionUpgrade = async (paymentMethod, selectedPackage) => {
    if (!auth.currentUser) {
      alert('Please log in to purchase credits');
      return;
    }

    // Use the selected package instead of looking for a pro subscription
    if (!selectedPackage) {
      alert('Please select a credit package');
      return;
    }

    try {
      if (paymentMethod === 'stripe') {
        // Ensure you have a valid Stripe price ID for the package
        if (!selectedPackage.stripePrice) {
          alert('No Stripe price configured for this package');
          return;
        }

        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({
          lineItems: [{
            price: selectedPackage.stripePrice,
            quantity: 1,
          }],
          mode: 'payment',
          successUrl: `${window.location.origin}/profile?purchase=success&credits=${selectedPackage.credits}`,
          cancelUrl: `${window.location.origin}/profile?purchase=cancelled`,
          customerEmail: auth.currentUser.email
        });

        if (error) {
          console.error('Stripe Checkout error:', error);
          alert(`Purchase failed: ${error.message}`);
        }
      } else if (paymentMethod === 'paypal') {
        // PayPal checkout logic
        const paypal = await loadScript({ 
          clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
          currency: 'USD',
          intent: 'capture'
        });

        const createOrder = (data, actions) => {
          return actions.order.create({
            purchase_units: [{
              amount: {
                value: selectedPackage.price.toString()
              },
              description: `${selectedPackage.credits} Credits Package`
            }]
          });
        };

        const onApprove = async (data, actions) => {
          try {
            const orderDetails = await actions.order.capture();
            
            // Create notification for successful purchase
            await createNotification(
              auth.currentUser.uid,
              'credits_purchase',
              `Successfully purchased ${selectedPackage.credits} Credits`,
              null
            );

            // Update user credits 
            alert(`Purchase successful! ${selectedPackage.credits} Credits added. Order ID: ${orderDetails.id}`);
          } catch (error) {
            console.error('PayPal approval error:', error);
            alert('Failed to complete purchase');
          }
        };

        const paypalButtonsComponent = paypal.Buttons({
          createOrder,
          onApprove,
          onError: (err) => {
            console.error('PayPal Checkout Error:', err);
            alert(`Payment failed: ${err.message}`);
          }
        });

        // Render PayPal buttons
        await paypalButtonsComponent.render('#paypal-button-container');
      }
    } catch (error) {
      console.error('Credit purchase error:', error);
      alert(`Failed to process purchase: ${error.message}`);
    }
  };

  useEffect(() => {
    const handleSubscriptionResult = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const subscriptionStatus = urlParams.get('subscription');

      if (subscriptionStatus === 'success') {
        try {
          // Update user profile to Pro
          const userDocRef = doc(db, 'profiles', auth.currentUser.uid);
          await updateDoc(userDocRef, {
            role: 'pro',
            subscriptionStartDate: serverTimestamp()
          });

          // Optional: Show success message
          alert('Successfully upgraded to Pro subscription!');

          // Refresh profile data
          await fetchProfileData();

          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error updating Pro subscription:', error);
        }
      } else if (subscriptionStatus === 'cancelled') {
        alert('Pro subscription upgrade was cancelled.');
      }
    };

    handleSubscriptionResult();
  }, []);

  const handleSaveAbout = async () => {
    if (!currentUser || !isProfileOwner) {
      alert('You can only edit your own profile bio.');
      return;
    }

    try {
      const userDocRef = doc(db, 'profiles', currentUser.uid);
      await updateDoc(userDocRef, {
        about: editedAbout.trim(),
        updatedAt: serverTimestamp()
      });

      setProfile(prev => ({
        ...prev,
        about: editedAbout.trim()
      }));

      setIsEditingAbout(false);

      await createNotification(
        currentUser.uid,
        'profile_update',
        'Your profile bio has been updated successfully!',
        null
      );
    } catch (error) {
      console.error('Error updating about:', error);
      alert('Failed to update profile bio. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-red-500 dark:text-red-400 text-xl mb-4">{error}</div>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-900 via-purple-900 to-black text-white overflow-hidden relative">
      <AnimatedBackground />
      {/* Profile Header */}
      <div className="relative">
        {/* Cover Image */}
        <div 
          className="h-48 bg-gradient-to-r from-purple-600 to-blue-600 relative group"
          style={{
            backgroundImage: profile?.coverImageUrl 
              ? `url(${profile.coverImageUrl})` 
              : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {(isProfileOwner || canEdit) && (
            <label 
              htmlFor="cover-image-upload"
              className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              <div className="absolute bottom-4 right-4 flex items-center space-x-2 bg-white/20 px-3 py-2 rounded-full">
                <FaCamera className="text-white text-xl" />
                <span className="text-white text-sm">Upload Cover</span>
              </div>
              <input 
                type="file" 
                id="cover-image-upload"
                accept="image/jpeg,image/png,image/gif"
                onChange={handleCoverImageUpload}
                className="hidden"
                disabled={isUploadingCoverImage}
              />
            </label>
          )}
        </div>

        {/* Cover Image Error Message */}
        {coverImageError && (
          <div className="text-center text-red-500 mb-4">
            {coverImageError}
          </div>
        )}

        {/* Profile Info Card */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative -mt-24">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                {/* Profile Picture */}
                <div className="w-32 h-32 -mt-16 sm:-mt-20 relative z-10 group">
                  <ProfilePicture
                    imageUrl={profile?.profilePic || profile?.photoURL}
                    size="lg"
                    className="w-full h-full rounded-full object-cover"
                  />
                  {(isProfileOwner || canEdit) && (
                    <label 
                      htmlFor="profile-picture-upload"
                      className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <input 
                        type="file" 
                        id="profile-picture-upload"
                        accept="image/jpeg,image/png,image/gif"
                        onChange={handleProfilePictureUpload}
                        className="hidden"
                        disabled={isUploadingProfilePic}
                      />
                      <FaCamera className="text-white text-2xl" />
                    </label>
                  )}
                </div>

                {/* Profile Info */}
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="relative group">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="text-2xl font-bold bg-transparent border-b border-purple-400 focus:border-purple-500 outline-none"
                          placeholder="Name"
                        />
                      ) : (
                        <div className="flex items-center justify-center sm:justify-start">
                          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {profile?.username || 'Anonymous'}
                          </h1>
                          {(isProfileOwner || canEdit) && (
                            <button
                              onClick={() => setIsEditing(true)}
                              className="ml-2 text-purple-400 hover:text-purple-300 transition-colors"
                            >
                              <FaUserEdit size={20} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {profile?.uid !== auth.currentUser?.uid && (
                      <FollowButton targetUserId={profile?.uid} />
                    )}
                  </div>

                  {/* Profile Details */}
                  <div className="mt-4 space-y-2">
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          value={editForm.location}
                          onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                          className="w-full bg-transparent border-b border-purple-400 focus:border-purple-500 outline-none"
                          placeholder="Location"
                        />
                        <input
                          type="email"
                          value={editForm.email}
                          disabled
                          className="w-full bg-transparent border-b border-purple-400 focus:border-purple-500 outline-none"
                          placeholder="Email"
                        />
                        <div className="flex justify-center sm:justify-start space-x-3">
                          <button
                            onClick={handleEdit}
                            className="px-4 py-1 bg-purple-500 text-white rounded-full hover:bg-purple-600 transition"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setIsEditing(false);
                              setEditForm({
                                name: profile?.name || '',
                                location: profile?.location || '',
                                username: profile?.username || '',
                                email: profile?.email || ''
                              });
                            }}
                            className="px-4 py-1 bg-gray-500 text-white rounded-full hover:bg-gray-600 transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {profile?.location && (
                          <p className="flex items-center justify-center sm:justify-start text-gray-600 dark:text-gray-300">
                            <FaMapMarkerAlt className="mr-2" />
                            {profile.location}
                          </p>
                        )}
                        {profile?.email && (
                          <p className="flex items-center justify-center sm:justify-start text-gray-600 dark:text-gray-300">
                            <FaEnvelope className="mr-2" />
                            {profile.email}
                          </p>
                        )}
                        {profile?.role && (
                          <p className="flex items-center justify-center sm:justify-start text-gray-600 dark:text-gray-300">
                            <FaUserTag className="mr-2" />
                            {profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {(isProfileOwner || canEdit) ? (
                    <div className="mt-4">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-semibold text-white">About</h3>
                        {!isEditingAbout ? (
                          <button 
                            onClick={() => setIsEditingAbout(true)}
                            className="text-purple-300 hover:text-purple-200"
                          >
                            <FaEdit />
                          </button>
                        ) : null}
                      </div>
                      
                      {isEditingAbout ? (
                        <div className="mt-2 space-y-2">
                          <textarea
                            value={editedAbout}
                            onChange={(e) => setEditedAbout(e.target.value)}
                            placeholder="Tell us about yourself..."
                            className="w-full bg-white/10 text-white rounded-lg p-3 border border-purple-500/30 focus:ring-2 focus:ring-purple-500/50 transition"
                            rows={4}
                            maxLength={500}
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={handleSaveAbout}
                              className="bg-purple-600 text-white px-4 py-2 rounded-full hover:bg-purple-700 transition"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditedAbout(profile?.about || '');
                                setIsEditingAbout(false);
                              }}
                              className="bg-gray-600 text-white px-4 py-2 rounded-full hover:bg-gray-700 transition"
                            >
                              Cancel
                            </button>
                          </div>
                          <p className="text-xs text-gray-400">
                            {editedAbout.length}/500 characters
                          </p>
                        </div>
                      ) : (
                        <p className="text-white/80 mt-2">
                          {profile?.about || 'No bio yet'}
                        </p>
                      )}
                    </div>
                  ) : profile?.about ? (
                    <div className="mt-4">
                      <h3 className="text-lg font-semibold text-white">About</h3>
                      <p className="text-white/80 mt-2">{profile.about}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          {(canViewCredits) && (
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 space-y-2">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setIsCreditsExpanded(!isCreditsExpanded)}
              >
                <div className="flex items-center">
                  <FaCrown className="mr-2 text-yellow-500" />
                  <span className="font-semibold">Credits Overview</span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                  {isCreditsExpanded ? '▼' : '▶'} 
                  Role: {profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                </span>
              </div>
              
              {isCreditsExpanded && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-3 shadow-sm">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Purchased Credits</p>
                      <p className="text-xl font-bold">{stats.credits || 0}</p>
                    </div>
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-3 shadow-sm">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Monthly Free Credits</p>
                      <p className="text-xl font-bold">
                        {FEATURE_PRICING.roles[profile.role].monthlyFreeCredits}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <p className="font-semibold">Purchase Additional Credits</p>
                    <div className="grid grid-cols-3 gap-4">
                      {CREDIT_PACKAGES.map((pkg) => (
                        <div 
                          key={pkg.id}
                          onClick={() => setSelectedPackage(pkg)}
                          className={`
                            cursor-pointer p-4 rounded-lg transition-all 
                            ${selectedPackage?.id === pkg.id 
                              ? 'bg-purple-600/40 border-2 border-purple-500 ring-2 ring-purple-300' 
                              : 'bg-white/10 hover:bg-white/20'}
                            relative
                          `}
                        >
                          {selectedPackage?.id === pkg.id && (
                            <div className="absolute top-2 right-2 bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                              ✓
                            </div>
                          )}
                          <p className="text-white font-bold text-lg">{pkg.credits} Credits</p>
                          <p className="text-white/70">€{pkg.price}</p>
                          <p className="text-white/80 text-sm">{pkg.description}</p>
                        </div>
                      ))}
                    </div>
                    {selectedPackage && (
                      <div className="mt-6 bg-white/10 p-6 rounded-lg">
                        <h3 className="text-2xl font-bold text-white mb-4">
                          Select Payment Method for {selectedPackage.name}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Stripe Payment Option */}
                          <button
                            onClick={() => {
                              setPaymentMethod('stripe');
                              handleProSubscriptionUpgrade('stripe', selectedPackage);
                            }}
                            className="flex items-center justify-center p-4 bg-white text-purple-600 rounded-lg hover:bg-purple-100 transition flex items-center justify-center space-x-2"
                          >
                            <FaCreditCard className="mr-2" /> 
                            Pay with Stripe
                          </button>

                          {/* PayPal Payment Option */}
                          <button
                            onClick={() => {
                              setPaymentMethod('paypal');
                              handlePayPalPayment(selectedPackage);
                            }}
                            className="flex items-center justify-center p-4 bg-white text-blue-600 rounded-lg hover:bg-blue-100 transition flex items-center justify-center space-x-2"
                          >
                            <FaPaypal className="mr-2" /> 
                            Pay with PayPal
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Content Tabs */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
          <button
            onClick={() => setActiveTab('public')}
            className={`px-4 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'public'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Public Dreams
          </button>
          <button
            onClick={() => setActiveTab('private')}
            className={`px-4 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'private'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Private Dreams
          </button>
        </div>

        {/* Posts Grid */}
        <div className="space-y-6 mb-8">
          {posts
            .filter(post => 
              activeTab === 'private' 
                ? post.visibility === 'private'
                : post.visibility !== 'private'
            )
            .map(post => (
              <Post key={post.id} post={post} />
            ))}
          
          {posts.filter(post => 
            activeTab === 'private' 
              ? post.visibility === 'private'
              : post.visibility !== 'private'
          ).length === 0 && (
            <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg shadow">
              <p className="text-gray-500 dark:text-gray-400">
                No {activeTab} dreams to display
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Profile Picture Error Message */}
      {profilePictureError && (
        <div className="text-center text-red-500 mb-4">
          {profilePictureError}
        </div>
      )}
      
      {/* Loading Indicator */}
      {isUploadingProfilePic && (
        <div className="text-center text-purple-300 mb-4">
          Uploading profile picture...
        </div>
      )}
      {isOwnProfile && (
        <div className="mt-4">
          <div className="credit-display">
            <p>Current Credits: {userCredits}</p>
            <button onClick={() => setShowPaymentOptions(true)}>
              Buy More Credits
            </button>
          </div>
        </div>
      )}
      {paymentModalOpen && (
        <PaymentModal 
          isOpen={paymentModalOpen} 
          onClose={() => setPaymentModalOpen(false)} 
          selectedPackage={selectedPackage} 
          onPaymentSuccess={() => {
            setPaymentModalOpen(false);
            setSelectedPackage(null);
          }} 
        />
      )}
      {!profile?.role || profile?.role !== 'pro' ? (
        <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl shadow-2xl p-6 mb-6 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-3xl">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-blue-700 opacity-10"></div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0 md:space-x-6">
            <div className="flex-grow text-center md:text-left">
              <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">
                Unlock Dreamarium Pro
              </h2>
              <p className="text-white/80 mb-4 max-w-xl">
                Elevate your dream creation experience with unlimited credits, priority features, and exclusive access.
              </p>
              <ul className="text-white/90 space-y-2 mb-4 inline-block text-left">
                <li className="flex items-center">
                  <span className="mr-2">✨</span> Unlimited Monthly Credits
                </li>
                <li className="flex items-center">
                  <span className="mr-2">🚀</span> Priority Feature Access
                </li>
                <li className="flex items-center">
                  <span className="mr-2">🎨</span> Exclusive Dream Generation Styles
                </li>
              </ul>
            </div>
            
            <div className="flex flex-col space-y-3 w-full md:w-auto">
              <button
                onClick={() => {
                  setSelectedPackage({
                    name: 'Pro Subscription',
                    price: 8,
                    credits: 'unlimited',
                    stripePrice: 'price_1QucBUFoEJrwHsIZYwq5NsmV'
                  });
                  setPaymentMethod('stripe');
                  setShowPaymentOptions(true);
                }}
                className="w-full md:w-auto px-6 py-3 bg-white text-purple-600 font-bold rounded-full hover:bg-purple-100 transition flex items-center justify-center space-x-2"
              >
                <FaCreditCard className="mr-2" /> 
                Upgrade with Stripe - $8/month
              </button>
              
              <button
                onClick={() => {
                  setSelectedPackage({
                    name: 'Pro Subscription',
                    price: 8,
                    credits: 'unlimited'
                  });
                  setPaymentMethod('paypal');
                  setShowPaymentOptions(true);
                }}
                className="w-full md:w-auto px-6 py-3 bg-white text-blue-600 font-bold rounded-full hover:bg-blue-100 transition flex items-center justify-center space-x-2"
              >
                <FaPaypal className="mr-2" /> 
                Upgrade with PayPal - $8/month
              </button>
            </div>
          </div>
          <button 
            onClick={() => {
              setShowPaymentOptions(false);
              setSelectedPackage(null);
              setPaymentMethod(null);
            }} 
            className="absolute top-4 right-4 text-white text-2xl hover:text-gray-200 transition-colors"
          >
            X
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default Profile;